'use client';
import { FC, HTMLAttributes, useEffect, useState } from 'react';
import { Path, useForm } from 'react-hook-form';
import { usePathname, useRouter } from 'next/navigation';
import { dayKeyOrInvalid, formatDate } from '@/lib/date-format';
import { parseDateString } from '@/lib/rome-date';

import { IExperienceDictionary } from '@/dictionaries/pages/experience/types';
import {
  Experience,
  ExperienceBookRequest,
  ExperienceMedia,
  ExperiencePrice,
  Options,
  VillaBooking,
} from '@/services/experienceTypes';
import { yupResolver } from '@hookform/resolvers/yup';
import { Tooltip } from 'react-tooltip';
import { getRequestDateRange } from './request-dates';

import QuantitieInput, { QuantityOption } from '../QuantitieInput';
import DatePicker from '../DatePicker';
import Select from '../Select';
import Button from '../Button';
import { ExperienceForm, getTranslatedSchema } from './schema';
import { Locale } from '../../../i18n.config';

import { IoCloseSharp } from 'react-icons/io5';
import { MdPlace } from 'react-icons/md';

import IVillageSideColumnDictionary from '@/dictionaries/components/villageSideColumn/types';
import { IQuantitiesInputDictonary } from '@/dictionaries/components/quantitiesInput/types';
import { IDatePickerDictonary } from '@/dictionaries/components/datePicker/types';
import { IContactPageFormDictionary } from '@/dictionaries/pages/contacts/types';
import { IModalsDictionary } from '@/dictionaries/components/modals/types';

import { saveBookRequest } from '@/actions/experiences';
import useExperienceContext from '@/hooks/useExperienceContext';
import useVillabooking from '@/hooks/useVillabookingContext';
import { useCurrency } from '@/contexts/currencyContext';
import useLocalStorage from '@/hooks/useLocalStorage';
import { toggleExperienceShortlist } from '@/utils';
import MediaFilterImage from '@/components/Media/MediaFilterImage';
import { formatLocalizedNumber } from '@/utils/village';

import VillageSideColumnEmailModal from '../VillageSideColumn/VillageSideColumnEmailModal';
import FilledStarIcon from '../Icons/FilledStar';
import EmptyStarIcon from '../Icons/EmptyStar';
import FacebookIcon from '../Icons/Facebook';
import TwitterIcon from '../Icons/Twitter';
import LinkedinIcon from '../Icons/Linkedin';

import { ShortList } from '../CategorySections/ExperienceType';
import LoginModal from '../LoginModal';
import { Loading } from '../Loading';

import styles from './styles.module.css';

interface IRequestCardProps extends HTMLAttributes<HTMLDivElement> {
  lang: Locale;
  dict: IExperienceDictionary;
  datePickerDict: IDatePickerDictonary;
  quantitiesInputDict: IQuantitiesInputDictonary;
  villageSideColumnDict: IVillageSideColumnDictionary;
  formDict: IContactPageFormDictionary;
  modalsDict: IModalsDictionary;
  title?: string;
  experience: Experience;
  experienceOptions: Options[];
  optionsTranslation: (
    | {
        id: string;
        title?: string;
        description?: string;
        locale?: string;
      }
    | undefined
  )[];
  media: ExperienceMedia[];
  villaBooking?: VillaBooking[];
  needLogin: boolean;
}

export const RequestCard: FC<IRequestCardProps> = ({
  lang,
  dict,
  datePickerDict,
  quantitiesInputDict,
  villageSideColumnDict,
  formDict,
  modalsDict,
  experience,
  title,
  experienceOptions,
  optionsTranslation,
  media,
  villaBooking,
  needLogin,
}) => {
  const today = new Date();
  const nextYear = new Date(today);
  nextYear.setFullYear(today.getFullYear() + 1);

  const [openRequest, setOpenRequest] = useState(false);
  const [times, setTimes] = useState(experienceOptions[0].times);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [adultsTotal, setAdulstTotal] = useState(experience.maxAdults);
  const [childrenTotal, setChildrenTotal] = useState(experience.maxChildren);
  const [infantsTotal, setInfantsTotal] = useState(experience.maxInfants);
  const [maxParticipants, setMaxParticipants] = useState(
    experience.maxParticipants,
  );
  const [totalPrice, setTotalPrice] = useState('');
  const [fromPrice, setFromPrice] = useState('');
  const [tooltipIsOpen, setTooltipIsOpen] = useState(false);
  const [selectedVillaBooking, setSelectedVillaBooking] =
    useState<VillaBooking>();
  const [bookError, setBookError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [prices, setPrices] = useState<ExperiencePrice[]>(
    experience.optionMaps[0].prices,
  );
  const [favourites, setFavourites] = useLocalStorage(
    'experience',
    [] as ShortList[],
  );
  const [villaBookingRange, setVillaBookingRange] = useState({
    start: today,
    end: nextYear,
  });

  const [villaBookingsAvailable, setVillaBookingsAvailable] = useState<
    VillaBooking[]
  >([]);

  const { currency } = useCurrency();
  const {
    inShortList,
    handleShortList,
    setEnquiryDate,
    setEnquiryAdultsTotal,
    setEnquiryChildrenTotal,
    setEnquiryInfantsTotal,
  } = useExperienceContext();
  const { isAvailable, changeIsAvailable } = useVillabooking();

  const router = useRouter();
  const pathname = usePathname();

  const languagesOptions = experience.languages;

  const reorderLanguages = (
    lang: string,
    languagesOptions: {
      id: number;
      code: string;
      description: string;
    }[],
  ) => {
    const defaultLang = 'en';

    const priorityLang = languagesOptions.some((item) => item.code === lang)
      ? lang
      : defaultLang;

    return languagesOptions.sort((a, b) =>
      a.code === priorityLang ? -1 : b.code === priorityLang ? 1 : 0,
    );
  };

  const reorderedOptions = reorderLanguages(lang, languagesOptions);

  const needContact = experience.id === 36;

  const villaBookings = villaBookingsAvailable.map((book) => {
    const label = `${book.villa.title} | ${formatDate(
      parseDateString(book.timestampStart),
      'dd/MM/yyyy',
    )} - ${formatDate(parseDateString(book.timestampCheckOut), 'dd/MM/yyyy')}`;
    return {
      label: label,
      value: book.id.toString(),
    };
  });

  const schema = getTranslatedSchema(dict.request.errors, experience);

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
    setError,
    getValues,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      villaBooking: {
        label: '',
        value: '',
      },
      option: {
        label: optionsTranslation[0]?.title,
        value: optionsTranslation[0]?.id,
      },
      time: {
        label: times[0],
        value: times[0],
      },
      language: {
        label: reorderedOptions[0].description,
        value: reorderedOptions[0].id.toString(),
      },
      quantities: {
        adults: 0,
        children: 0,
        infants: 0,
      },
    },
  });

  const confirmedDates = watch('dates');
  const selectedInfants = watch('quantities.infants');
  const selectedAdults = watch('quantities.adults');
  const selectedChildren = watch('quantities.children');

  const totalParticipants = selectedAdults + selectedChildren + selectedInfants;

  function updateQuantitiesTotal() {
    const maxParticipants =
      selectedVillaBooking &&
      selectedVillaBooking.villa.sleeps < experience.maxParticipants
        ? selectedVillaBooking.villa.sleeps
        : experience.maxParticipants;

    const childrenAndInfantsSum = selectedChildren + selectedInfants;
    const adultsAndChildrenSum = selectedAdults + selectedChildren;
    const adultsAndInfantsSum = selectedAdults + selectedInfants;

    const newAdultsTotal = Math.min(
      experience.maxAdults,
      maxParticipants - childrenAndInfantsSum,
    );
    const newChildrenTotal = Math.min(
      experience.maxChildren,
      maxParticipants - adultsAndInfantsSum,
    );
    const newInfantsTotal = Math.min(
      experience.maxInfants,
      maxParticipants - adultsAndChildrenSum,
    );

    setMaxParticipants(maxParticipants);
    setChildrenTotal(newChildrenTotal);
    setAdulstTotal(newAdultsTotal);
    setInfantsTotal(newInfantsTotal);

    setEnquiryAdultsTotal(selectedAdults);
    setEnquiryChildrenTotal(selectedChildren);
    setEnquiryInfantsTotal(selectedInfants);
  }

  const coverMedia = media.find((m) => m.type === 'cover');

  const options = optionsTranslation.map((item) => {
    return {
      label: item?.title ? item.title : '',
      value: item?.id ? item.id : '',
    };
  });

  const time = times.map((item) => {
    return {
      label: item,
      value: item,
    };
  });

  const language = reorderedOptions.map((item) => {
    return {
      label: item.description,
      value: item.id.toString(),
    };
  });

  const quantitiesInputOptions: QuantityOption[] = [
    { label: quantitiesInputDict.adultsLabel, name: 'adults', value: 0 },
    {
      label: quantitiesInputDict.childrenLabel,
      name: 'children',
      value: 0,
    },
    { label: quantitiesInputDict.infantsLabel, name: 'infants', value: 0 },
  ];

  const addToShortlist = (
    <div className="flex justify-center w-full">
      <div
        className="flex w-auto text-primary font-averta-semibold gap-2 justify-center cursor-pointer "
        onClick={() =>
          toggleExperienceShortlist(
            experience,
            lang,
            favourites,
            setFavourites,
            inShortList,
            currency,
          )
        }>
        {inShortList ? (
          <>
            <FilledStarIcon className="fill-current text-inherit w-6 h-6" />
            <p>{dict.request.removeShortlist}</p>
          </>
        ) : (
          <>
            <EmptyStarIcon className="fill-current text-inherit w-6 h-6" />
            <p>{dict.request.addShortlist}</p>
          </>
        )}
      </div>
    </div>
  );

  const changeOption = (value: { label: string; value: string }) => {
    experienceOptions.map((item) => {
      const optionMap = experience.optionMaps.find((optionMap) => {
        if (optionMap.villaId || selectedVillaBooking?.villa.id) {
          return (
            optionMap.optionId === parseInt(value.value) &&
            optionMap.villaId === selectedVillaBooking?.villa.id
          );
        }
        return optionMap.optionId === parseInt(value.value);
      });

      if (optionMap) {
        setPrices(optionMap.prices);
      }

      const times = item.times;

      item.translations.find((item: any) => {
        if (item.title === value.label) {
          setTimes(times);
          setValue('time', {
            label: times[0],
            value: times[0],
          });
        }
      });
    });
  };

  const formatPrice = (price: number) => {
    return `${currency.symbol} ${formatLocalizedNumber(price, lang)}`;
  };

  const { minDate, maxDate } = getRequestDateRange(experience);

  const onSubmit = handleSubmit(async (data) => {
    if (data.quantities.adults < 1) {
      setError('quantities', {
        type: 'custom',
        message: dict.request.errors.adults,
      });
      return;
    }

    const body: ExperienceBookRequest = {
      adults: data.quantities.adults,
      children: data.quantities.children,
      infants: data.quantities.infants,
      experience: experience.id,
      selectedDate: dayKeyOrInvalid(data.dates.start),
      selectedDateEnd: dayKeyOrInvalid(data.dates.end),
      selectedLanguage: Number(data.language.value),
      selectedOption: Number(data.option.value),
      selectedTime: data.time.value,
      villaBooking:
        data.villaBooking.value === '' ? null : Number(data.villaBooking.value),
      type: 'cart',
      ownerConfirmationStatus: 'pending',
      totalPrice: totalPrice,
      totalOwnerPrice: totalPrice,
      createdAt: formatDate(new Date(), 'yyyy-MM-dd HH:mm:ss'),
      updatedAt: formatDate(new Date(), 'yyyy-MM-dd HH:mm:ss'),
    };

    try {
      setIsLoading(true);
      // Don't change this function call (the true is to call devsite method);
      const response = await saveBookRequest(body, true);

      if (response.id) {
        router.push(
          `${lang === 'en' ? '' : `/${lang}`}/book-experience?booking_id=${
            response.id
          }`,
        );
        setIsLoading(false);
        setOpenRequest(false);
      } else {
        console.error(response);
        setIsLoading(false);
        setBookError(true);
      }
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    }
  });

  const onClickEnquiry = () => {
    setOpenRequest(false);
    const element = document.getElementById('enquiry');

    if (element) {
      const topOffset = element.offsetTop - 80;
      window.scrollTo({ top: topOffset, behavior: 'smooth' });
    }
  };

  const handleConfirmDates = (dates: {
    start: Date | null;
    end: Date | null;
  }) => {
    setValue('dates', dates);
    setEnquiryDate(dates);
  };

  useEffect(() => {
    const isFavourite = favourites.some((item) => item.id === experience.id);

    if (isFavourite) {
      handleShortList(true);
    } else {
      handleShortList(false);
    }
    // eslint-disable-next-line
  }, [favourites]);

  useEffect(() => {
    if (openRequest) {
      document.documentElement.classList.add('overflow-y-hidden');
    } else {
      document.documentElement.classList.remove('overflow-y-hidden');
    }
  }, [openRequest]);

  useEffect(() => {
    let closestPrice: any = null;
    const participants = Math.max(
      experience.minParticipants,
      selectedAdults + selectedChildren,
    );

    prices.forEach((row) => {
      if (
        participants >= row.minParticipants &&
        participants <= row.maxParticipants
      ) {
        closestPrice = row.price;
      }
    });

    const price = closestPrice ? closestPrice : '0';

    setTotalPrice(price);
    updateQuantitiesTotal();
    // eslint-disable-next-line
  }, [totalParticipants, prices]);

  useEffect(() => {
    let lowestPrice: number | null = null;
    if (needLogin) {
      experience.optionMaps.forEach((option) => {
        option.prices.forEach((priceObj) => {
          const price = parseFloat(priceObj.price);
          if (lowestPrice === null || price < lowestPrice) {
            setFromPrice(price.toString());
            lowestPrice = price;
          }
        });
      });
    } else {
      experience.optionMaps.forEach((option) => {
        option.prices.forEach((priceObj) => {
          const price = parseFloat(priceObj.price);
          if (lowestPrice === null || price < lowestPrice) {
            setFromPrice(price.toString());
            lowestPrice = price;
          }
        });
      });
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const villaBookingId = watch('villaBooking.value');
    villaBooking?.find((book) => {
      if (book.id === Number(villaBookingId)) {
        setVillaBookingRange({
          start: new Date(book.timestampStart),
          end: new Date(book.timestampCheckOut),
        });
        setSelectedVillaBooking(book);
        setMaxParticipants(book.villa.sleeps);
      }
    });
    // eslint-disable-next-line
  }, [watch('villaBooking.value')]);

  useEffect(() => {
    let bookings: any = [];
    let addedIds = new Set();

    try {
      experience.optionMaps.forEach((option) => {
        villaBooking?.forEach((book) => {
          if (option.villaId === book.villa.id && !addedIds.has(book.id)) {
            bookings.push(book);
            addedIds.add(book.id);
          }
        });
      });
    } catch (error) {
      console.log('EXPERIENCE-VB', error);
    }

    if (bookings.length > 0) {
      setVillaBookingsAvailable(bookings);
      changeIsAvailable(true);
      setValue('villaBooking', {
        label: `${bookings[0].villa.title} | ${formatDate(
          parseDateString(bookings[0].timestampStart),
          'dd/MM/yyyy',
        )} - ${formatDate(
          parseDateString(bookings[0].timestampCheckOut),
          'dd/MM/yyyy',
        )}`,
        value: bookings[0].id.toString(),
      });
    } else {
      changeIsAvailable(false);
    }
    // eslint-disable-next-line
  }, [setValue]);

  useEffect(() => {
    const optionMap = experience.optionMaps.filter(
      (optionMap) => optionMap.villaId === selectedVillaBooking?.villa.id,
    );

    const currentOption = Number(getValues('option.value'));

    const selectedOption = optionMap.find(
      (option) => option.optionId === currentOption,
    );

    if (selectedOption) {
      setPrices(selectedOption.prices);
    }
    // eslint-disable-next-line
  }, [selectedVillaBooking?.villa.id]);

  return (
    <>
      <div className="desktop:hidden fixed -bottom-[2px] flex justify-between px-[15px] tablet:px-10 py-[15px] bg-white w-full mx-[-0.9375rem] sm:mx-[-2.5rem] z-[20] shadow-[0_20px_50px_0px_rgba(0,0,0,0.5)]">
        <div className="flex flex-col justify-center w-1/2">
          {experience.id !== 36 && (
            <>
              <p>{dict.request.from}</p>
              <p className="text-medium font-noe">
                {formatPrice(Number(fromPrice))}
              </p>
            </>
          )}
        </div>
        <Button
          className="w-auto px-5 py-4"
          onClick={() => setOpenRequest(true)}>
          {dict.request.book}
        </Button>
      </div>

      <div
        id="request"
        className={
          openRequest
            ? 'fixed top-0 left-0 h-full w-full bg-white z-[999] desktop:z-[30] overflow-y-scroll desktop:block desktop:relative desktop:min-w-[490px] desktop:-ml-20'
            : 'hidden max-h-[50%] bg-white desktop:flex desktop:flex-col desktop:relative desktop:min-w-[490px] desktop:-ml-20 desktop:basis-auto'
        }>
        {openRequest && (
          <div
            className="absolute top-[10px] right-[15px] cursor-pointer z-[50]"
            onClick={() => setOpenRequest(false)}>
            <IoCloseSharp className="w-6 h-6 text-white" />
          </div>
        )}
        <div className="desktop:border-b desktop:border-r 2xl:border-x desktop:border-primary desktop:shadow-default desktop:sticky desktop:top-[80px] desktop:z-10">
          <div className={styles['book-box']}>
            <MediaFilterImage
              media={coverMedia}
              filterName="medium_2-1"
              alt="Photo cover experience"
              fill
              className="opacity-100 transition-all duration-300 ease-in-out w-full h-full object-cover object-[50%_50%] inline-block align-middle max-w-full"
              sizes="(min-width: 1280px) 80vw, (min-width: 767px) 90vw, (min-width: 480px) 100vw, 100vw"
              style={{
                objectFit: 'cover',
              }}
            />
            <div className="absolute flex flex-col items-center justify-center w-full h-full top-0 left-0 bg-black/[.5]">
              <h3 className="text-center text-white text-medium font-noe">
                {title}
              </h3>
              <span className={styles['book-city']}>
                <MdPlace className="w-5 h-5 mr-2 mt-[2px]" />
                {experience.city ? experience.city : dict.yourVilla}
              </span>
            </div>
          </div>

          {!needLogin ? (
            experience.inVilla && !isAvailable ? (
              <div className="flex flex-col px-4 py-6">
                <div className="flex flex-col items-center">
                  <h4 className="font-noe text-xl mb-2 text-center">
                    {dict.request.notAvailable.title}
                  </h4>
                  <p className="text-center">
                    {dict.request.notAvailable.subtitle}
                  </p>
                </div>
                <div className="flex py-8 gap-4">
                  <Button
                    variant={'outline'}
                    className="px-0 hover:bg-white hover:text-primary"
                    onClick={() => onClickEnquiry()}>
                    {dict.request.enquiry}
                  </Button>
                </div>
                {addToShortlist}
              </div>
            ) : (
              <div className="relative flex flex-col px-4 py-6">
                {isLoading && (
                  <div className="absolute top-0 left-0 flex justify-center items-center w-full h-full bg-white/50 z-50">
                    <Loading />
                  </div>
                )}
                <div>
                  <h3 className="text-[32px] text-center font-noe">
                    {totalPrice && formatPrice(Number(totalPrice))}
                  </h3>
                  <form className="flex flex-col gap-4" onSubmit={onSubmit}>
                    {villaBookingsAvailable.length > 0 &&
                      experience.inVilla && (
                        <Select
                          id="villaBooking"
                          label={dict.yourVilla}
                          options={villaBookings}
                          onChange={(value) => {
                            setValue('villaBooking', value);
                            setValue('dates', { start: null, end: null });
                          }}
                          selectedValue={watch('villaBooking')}
                        />
                      )}
                    {experience.options.length > 1 && (
                      <Select
                        id="options"
                        label={dict.request.options}
                        options={options}
                        onChange={(value) => {
                          setValue('option', value);
                          changeOption(value);
                        }}
                        selectedValue={watch('option')}
                      />
                    )}
                    <div className="flex flex-col desktop:flex-row w-full gap-4">
                      <DatePicker.Root
                        open={modalIsOpen}
                        onOpenChange={setModalIsOpen}>
                        <div className="w-full desktop:w-1/2">
                          <DatePicker.Trigger
                            dict={datePickerDict}
                            confirmedDates={confirmedDates}
                            isOneDate
                          />
                        </div>
                        <DatePicker.Content
                          datePickerIsOpen={modalIsOpen}
                          confirmedDates={confirmedDates}
                          confirmDates={handleConfirmDates}
                          onOpenChange={setModalIsOpen}
                          dict={datePickerDict}
                          isOneDate
                          experienceId={experience.id}
                          inVillaRange={
                            experience.inVilla ? villaBookingRange : null
                          }
                          totalExperienceRange={
                            experience.inVilla
                              ? null
                              : { start: minDate, end: maxDate }
                          }
                        />
                      </DatePicker.Root>
                      <Select
                        id="time"
                        label={dict.request.time}
                        className="w-full desktop:w-1/2"
                        options={time}
                        onChange={(value) => {
                          setValue('time', value);
                        }}
                        selectedValue={watch('time')}
                      />
                    </div>
                    <div className="flex flex-col desktop:flex-row w-full gap-4">
                      <QuantitieInput
                        id="quantities"
                        label={dict.request.participants}
                        className="w-full desktop:w-1/2"
                        dict={quantitiesInputDict}
                        register={register}
                        infantsTotal={infantsTotal}
                        adultsTotal={adultsTotal}
                        childrenTotal={childrenTotal}
                        onChange={({ value, name }) => {
                          setValue(
                            `quantities.${name}` as Path<ExperienceForm>,
                            value,
                          );
                        }}
                        options={quantitiesInputOptions}
                        addedQuantities={watch('quantities')}
                        footerText={
                          <p className="text-xs">
                            {dict.request.youCanAdd}{' '}
                            <strong>
                              {`${maxParticipants} ${dict.request.participants}.`}
                            </strong>
                          </p>
                        }
                        onlyNumber
                      />
                      <Select
                        id="language"
                        label={dict.request.language}
                        className="w-full desktop:w-1/2"
                        options={language}
                        onChange={(value) => {
                          setValue('language', value);
                        }}
                        selectedValue={watch('language')}
                      />
                    </div>
                    <div className="flex flex-col">
                      {errors.dates && (
                        <div className="flex flex-col w-full justify-center">
                          <span className="text-error-primary text-center">
                            {errors.dates?.message?.toString()}
                          </span>
                        </div>
                      )}
                      {errors.quantities && !errors.dates && (
                        <div className="flex flex-col w-full justify-center">
                          <span className="text-error-primary text-center">
                            {errors.quantities?.message?.toString()}
                            {totalParticipants < experience.minParticipants &&
                              errors.quantities?.root?.message?.toString()}
                          </span>
                        </div>
                      )}
                      {bookError && (
                        <div className="flex flex-col w-full justify-center">
                          <span className="text-error-primary text-center">
                            {dict.request.errors.bookingError}
                          </span>
                        </div>
                      )}
                      <div className="flex py-8 gap-4">
                        <Button
                          type="button"
                          variant={'outline'}
                          className="px-4 hover:bg-white hover:text-primary"
                          onClick={onClickEnquiry}>
                          {dict.request.enquiry}
                        </Button>
                        <Button
                          type="submit"
                          className="px-4 hover:text-white hover:bg-primary">
                          {dict.request.book}
                        </Button>
                      </div>
                    </div>
                  </form>
                  <div className="flex flex-col gap-8">
                    {addToShortlist}
                    <div className="w-full mt-auto flex justify-around">
                      <div className="text-center">
                        <a
                          className="text-gray-700 text-xs uppercase"
                          href={
                            lang != 'en'
                              ? `/${lang}/how-to-book`
                              : `/how-to-book`
                          }
                          title="How to book"
                          target="_blank">
                          {dict.request.howToBook}
                        </a>
                      </div>
                      <VillageSideColumnEmailModal
                        id={experience.id}
                        type={'experiences'}
                        villageSideColumnDict={villageSideColumnDict}
                        formDict={formDict}
                      />
                      <div
                        onClick={() => setTooltipIsOpen((prev) => !prev)}
                        data-tooltip-id="share-tooltip"
                        className="text-center cursor-pointer">
                        <span
                          className="text-gray-700 text-xs uppercase"
                          title="How to book">
                          {dict.request.share}
                        </span>
                      </div>
                      <Tooltip
                        isOpen={tooltipIsOpen}
                        clickable
                        style={{
                          backgroundColor: 'white',
                          boxShadow: '0 2px 12px 0 rgba(0,0,0,.1)',
                          padding: '12px',
                        }}
                        border="1px solid #ebeef5"
                        id="share-tooltip"
                        openOnClick>
                        <div className="flex justify-between gap-4">
                          <a
                            target="_blank"
                            href={`https://www.facebook.com/sharer/sharer.php?u=https://www.villatravellers.com${pathname}`}>
                            <FacebookIcon className="w-4 h-4 fill-current text-gray-400 cursor-pointer" />
                          </a>
                          <a
                            target="_blank"
                            href={`https://twitter.com/intent/tweet?text=https://www.villatravellers.com${pathname}`}>
                            <TwitterIcon className="w-5 h-5 fill-current text-gray-400 cursor-pointer" />
                          </a>
                          <a
                            target="_blank"
                            href={`https://www.linkedin.com/sharing/share-offsite/?url=www.villatravellers.com${pathname}`}>
                            <LinkedinIcon className="w-4 h-4 fill-current text-gray-400 cursor-pointer" />
                          </a>
                        </div>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </div>
            )
          ) : (
            <div className="flex flex-col px-4 py-6">
              <div className="flex flex-col items-center">
                <h4 className="font-noe text-xl mb-2 text-center">
                  {needContact
                    ? dict.request.notLogged.contactTitle
                    : dict.request.notLogged.title}
                </h4>
                <p className="text-center">
                  {needContact
                    ? dict.request.notLogged.contactSubtitle
                    : dict.request.notLogged.subtitle}
                </p>
              </div>
              <div className="flex py-8 gap-4">
                <Button
                  variant={'outline'}
                  className="px-0 hover:bg-white hover:text-primary"
                  onClick={() => onClickEnquiry()}>
                  {dict.request.enquiry}
                </Button>
                {!needContact && (
                  <LoginModal
                    dict={modalsDict}
                    className="px-0 bg-primary text-white hover:bg-primary hover:text-white"
                  />
                )}
              </div>
              {addToShortlist}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
